/**
 * Adds minutes to a date
 * @param date The date to add minutes to
 * @param minutes The number of minutes to add
 * @returns A new date with the minutes added
 */
export function addMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60000);
}

/**
 * Subtracts minutes from a date
 * @param date The date to subtract minutes from
 * @param minutes The number of minutes to subtract
 * @returns A new date with the minutes subtracted
 */
export function subtractMinutes(date: Date, minutes: number): Date {
  return new Date(date.getTime() - minutes * 60000);
}

/**
 * Rounds a date to the nearest 5 minutes
 * @param date The date to round
 * @param direction The direction to round ('up' or 'down')
 * @returns A new date rounded to the nearest 5 minutes
 */
export function roundToNearest5Minutes(date: Date, direction: 'up' | 'down' = 'up'): Date {
  const result = new Date(date);
  const minutes = result.getMinutes();
  const remainder = minutes % 5;
  
  if (remainder === 0) return result;
  
  if (direction === 'up') {
    result.setMinutes(minutes + (5 - remainder));
  } else {
    result.setMinutes(minutes - remainder);
  }
  
  return result;
}

/**
 * Rounds a date to the nearest `stepMinutes` boundary.
 * Always clears seconds and milliseconds so the result lands on a clean minute.
 * Rounding 'up' from a value already exactly on the boundary returns the same time.
 * @param date The date to round
 * @param stepMinutes The step size in minutes (e.g. 5, 15, 30)
 * @param direction The direction to round ('up' or 'down'), defaults to 'up'
 * @returns A new date rounded to the requested boundary
 */
export function roundToStepMinutes(
  date: Date,
  stepMinutes: number,
  direction: 'up' | 'down' = 'up'
): Date {
  const result = new Date(date);
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) {
    result.setSeconds(0, 0);
    return result;
  }

  const minutes = result.getMinutes();
  const remainder = minutes % stepMinutes;
  const hasSubMinute = result.getSeconds() > 0 || result.getMilliseconds() > 0;
  result.setSeconds(0, 0);

  if (remainder === 0 && !hasSubMinute) return result;

  if (direction === 'up') {
    const bump = remainder === 0 ? 0 : stepMinutes - remainder;
    if (hasSubMinute && remainder === 0) {
      result.setMinutes(minutes + stepMinutes);
    } else {
      result.setMinutes(minutes + bump);
    }
  } else {
    result.setMinutes(minutes - remainder);
  }

  return result;
}

/**
 * Parses a local-date string (YYYY-MM-DD) plus an optional HH:MM time into a
 * `Date` interpreted in the browser's local timezone. This avoids the common
 * pitfall of `new Date("2025-08-12")` being parsed as UTC midnight.
 *
 * Returns `null` when the input cannot be parsed.
 */
export function parseLocalDateTime(dateStr: string, timeStr?: string): Date | null {
  if (!dateStr) return null;
  const dateMatch = dateStr.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!dateMatch) return null;
  const [, yStr, moStr, dStr] = dateMatch;
  const year = Number(yStr);
  const month = Number(moStr) - 1;
  const day = Number(dStr);

  let hour = 0;
  let minute = 0;
  if (timeStr) {
    const timeMatch = timeStr.match(/^(\d{1,2}):(\d{1,2})/);
    if (!timeMatch) return null;
    hour = Number(timeMatch[1]);
    minute = Number(timeMatch[2]);
  }

  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
  return new Date(year, month, day, hour, minute, 0, 0);
}

/**
 * Formats a `Date` as a compact, floating (timezone-less) calendar stamp:
 * `YYYYMMDDTHHMMSS`. Intended for building "add to calendar" links/ICS where
 * the wall-clock time should be preserved as-is for a local customer. Uses the
 * Date's local parts so the time matches what the user saw on screen.
 */
export function formatCalendarStamp(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
    `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  );
}

/**
 * Parses a backend timestamp returned by the .NET API. ABP serializes UTC
 * timestamps without a trailing `Z` (e.g. "2026-05-14T13:00:00.123"), which
 * causes `new Date(...)` to interpret them as local time and produce a
 * timezone-shifted value. This helper detects naked UTC strings and appends
 * `Z` so they parse as UTC.
 *
 * Returns `null` when the input is missing or cannot be parsed.
 */
export function parseBackendDate(value: Date | string | null | undefined): Date | null {
  if (value == null) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  let str = value;
  if (
    typeof str === 'string' &&
    str.includes('T') &&
    !str.endsWith('Z') &&
    !str.includes('+') &&
    !str.includes('-', str.indexOf('T'))
  ) {
    str = str + 'Z';
  }
  const parsed = new Date(str);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * True when a backend timestamp is .NET's unset `DateTime` sentinel
 * (`0001-01-01T00:00:00`) rather than a real point in time. ABP serializes
 * never-assigned non-nullable `DateTime` columns this way, and formatting one
 * naively renders the meaningless "1/1/1, 12:00:00 AM".
 */
export function isUnsetBackendDate(value: Date | string | null | undefined): boolean {
  const parsed = parseBackendDate(value);
  return parsed === null || parsed.getUTCFullYear() <= 1;
}

/**
 * True when a backend timestamp is earlier than the current moment. Missing or
 * unparsable values return `false`. Same parsing rules as {@link parseBackendDate}.
 */
export function isBeforeNow(value: Date | string | null | undefined): boolean {
  const parsed = parseBackendDate(value);
  return parsed !== null && parsed.getTime() < Date.now();
}

/**
 * Formats a Date as a short date + time label (e.g. "Aug 17, 2026, 12:33 PM").
 */
export function formatDateTimeShort(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (timezone) options.timeZone = timezone;
  return date.toLocaleString('en-US', options);
}

/**
 * Formats a "last synced" backend timestamp for display. A just-connected
 * integration has no sync timestamp yet, so the current date/time is shown
 * instead of the unset sentinel.
 */
export function formatLastSyncedTime(
  value: Date | string | null | undefined,
  timezone?: string
): string {
  const parsed = parseBackendDate(value);
  const effective = parsed && !isUnsetBackendDate(parsed) ? parsed : new Date();
  return formatDateTimeShort(effective, timezone);
}

/**
 * Formats a `Date` as a zero-padded 24-hour "HH:MM" time-of-day string.
 * When `timezone` is provided, the time is rendered in that IANA timezone
 * (e.g. "America/New_York"); otherwise the browser's local timezone is used.
 *
 * Use this for storing/comparing times of day extracted from a UTC `Date`
 * in a specific timezone (e.g. the tenant's timezone for availability
 * windows). Pair with `formatTimeString` / `formatTimeStringCompact` for
 * 12-hour display.
 */
export function formatTimeOfDay24(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
  if (timezone) options.timeZone = timezone;
  const formatted = new Intl.DateTimeFormat('en-GB', options).format(date);
  return formatted === '24:00' ? '00:00' : formatted;
}

/**
 * Formats a date to a time string (e.g., "10:30 AM")
 * @param date The date to format
 * @param timezone Optional IANA timezone (e.g., "America/New_York"). Defaults to browser timezone when omitted.
 * @returns A formatted time string
 */
export function formatTime(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
  if (timezone) {
    options.timeZone = timezone;
  }
  return date.toLocaleTimeString('en-US', options);
}

/**
 * Formats a Date as a short weekday name (e.g. "Mon", "Tue").
 */
export function formatWeekdayShort(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

/**
 * Formats a Date as a short month name (e.g. "Jan", "Feb").
 */
export function formatMonthShort(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short' });
}

/**
 * Formats a Date as a long, friendly label (e.g. "Wed, May 14, 2026").
 */
export function formatDateLong(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a Date as a compact label with the year and no weekday
 * (e.g. "May 14, 2026"). Use in dense tables where `formatDateLong`'s weekday
 * prefix costs more width than it earns.
 */
export function formatDateShort(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a Date as a medium label without the year (e.g. "Wed, May 14").
 */
export function formatDateMedium(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Formats a Date as a full label without the year (e.g. "Wednesday, May 14").
 */
export function formatDateFull(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a Date as a full label with the year (e.g. "Wednesday, May 14, 2026").
 */
export function formatDateFullWithYear(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Formats a Date as a long weekday name (e.g. "Sunday").
 */
export function formatWeekdayLong(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

/**
 * Formats a Date as a long month + day label (e.g. "July 5").
 */
export function formatMonthDayLong(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

/**
 * Formats a Date as a long month + year label (e.g. "October 2026").
 */
export function formatMonthYearLong(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

/**
 * Returns local midnight on the first day of the month `monthOffset` months
 * away from `date` (0 = same month). Always lands on day 1, so stepping from
 * Jan 31 by +1 yields Feb 1 rather than overflowing into March.
 */
export function startOfLocalMonth(date: Date, monthOffset: number = 0): Date {
  return new Date(date.getFullYear(), date.getMonth() + monthOffset, 1);
}

/**
 * Builds a Sunday-first month grid for a calendar picker: leading `null`s pad
 * the first week, followed by a local-midnight Date for every day in the month.
 */
export function getCalendarMonthGrid(month: Date): (Date | null)[] {
  const first = startOfLocalMonth(month);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const cells: (Date | null)[] = Array.from({ length: first.getDay() }, () => null);
  for (let day = 1; day <= daysInMonth; day++) {
    cells.push(new Date(first.getFullYear(), first.getMonth(), day));
  }
  return cells;
}

/**
 * Returns the local calendar parts (year, month 1-12, day 1-31) of a Date.
 * Use this instead of inlining getFullYear/getMonth/getDate when you need to
 * match a Date against a calendar-day payload (e.g. an external API's
 * { year, month, day } record).
 */
export function getLocalDateParts(date: Date): { year: number; month: number; day: number } {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
}

/**
 * Formats a Date as a compact "Mon DD" chart-axis label (e.g. "May 14").
 * When `timezone` is provided, the date is formatted in that IANA timezone
 * (e.g. "UTC" for platform-wide aggregations); otherwise the browser's local
 * timezone is used. Use this for x-axis labels in time-series charts instead
 * of inlining `toLocaleDateString` at the call site.
 */
export function formatChartDayLabel(date: Date, timezone?: string): string {
  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  if (timezone) options.timeZone = timezone;
  return date.toLocaleDateString('en-US', options);
}

/**
 * Parses a "YYYY-MM-DD" date-only string as UTC midnight on that calendar day.
 * Returns `null` if the input is missing or cannot be parsed.
 *
 * Useful when we receive day-keyed aggregates from the backend ("2026-04-30")
 * and need to render them on a chart in a fixed timezone (typically UTC) so
 * that the x-axis label matches the backend's bucketing rule.
 */
export function parseUtcDateKey(dateKey: string | null | undefined): Date | null {
  if (!dateKey) return null;
  const match = dateKey.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!match) return null;
  const [, y, mo, d] = match;
  const parsed = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Returns true when the provided value falls on today's calendar date in the
 * browser's local timezone. Strings are compared by their leading
 * "YYYY-MM-DD" portion (covers both date-only keys and ISO timestamps whose
 * wall-clock date is what the user sees on screen), so a slot's
 * `suggestedDate` never gets shifted across midnight by UTC parsing.
 *
 * Use this to decide whether to show "Today" affordances next to a displayed
 * appointment date.
 */
/**
 * Formats a Date as a "YYYY-MM-DD" calendar key using its local calendar parts.
 * This is the inverse of {@link parseLocalDateTime} and the correct value to
 * feed an `<input type="date">` or a date-only API query parameter — using
 * `toISOString().slice(0, 10)` instead shifts the day for anyone west of UTC.
 */
export function formatDateKey(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function toCalendarDateKey(value: Date | string | null | undefined): string | null {
  if (!value) return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return formatDateKey(value);
  }
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${match[1]}-${match[2]}-${match[3]}` : null;
}

/**
 * Returns a new Date shifted by whole days, preserving the local time of day.
 */
export function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Returns a Date at local midnight (00:00:00.000) on the same calendar day.
 */
export function startOfLocalDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
}

/**
 * Returns a Date at the last representable instant (23:59:59.999) of the same
 * local calendar day. Use for the exclusive-feeling upper bound of a
 * "whole days" range filter.
 */
export function endOfLocalDay(date: Date): Date {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
}

/**
 * Returns the hour of day (0–23) of an instant, read in the given IANA
 * timezone. Falls back to the browser's local hour when no timezone is given.
 * Use for bucketing appointments into time-of-day bands.
 */
export function getHourInTimezone(date: Date, timezone?: string): number {
  if (!timezone) return date.getHours();
  const parts = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    hour12: false,
    timeZone: timezone,
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === 'hour')?.value);
  if (!Number.isFinite(hour)) return date.getHours();
  return hour === 24 ? 0 : hour;
}

export function isCalendarToday(value: Date | string | null | undefined): boolean {
  const key = toCalendarDateKey(value);
  return key !== null && key === toCalendarDateKey(new Date());
}

/**
 * Returns the whole calendar-day difference between the provided value and
 * today in the browser's local timezone (0 = today, 1 = tomorrow, -1 =
 * yesterday). Compares calendar dates rather than millisecond deltas, so a
 * 2 PM slot later today is 0 days out — never rounded up to "tomorrow".
 * Strings are read by their leading "YYYY-MM-DD" portion, same as
 * {@link isCalendarToday}.
 *
 * Returns `null` when the input is missing or cannot be parsed.
 */
export function calendarDaysFromToday(value: Date | string | null | undefined): number | null {
  const key = toCalendarDateKey(value);
  if (!key) return null;
  return daysBetweenDateKeys(toCalendarDateKey(new Date()) as string, key);
}

/**
 * True when the value lands on a calendar day before today in the browser's
 * local timezone. Today is not past. Missing or unparsable values return
 * `false`. Same parsing rules as {@link isCalendarToday}.
 */
export function isCalendarPast(value: Date | string | null | undefined): boolean {
  const days = calendarDaysFromToday(value);
  return days !== null && days < 0;
}

/**
 * Calculates the time difference between two dates in minutes
 * @param date1 The first date
 * @param date2 The second date
 * @returns The difference in minutes
 */
export function getTimeDifferenceInMinutes(date1: Date, date2: Date): number {
  return Math.round((date2.getTime() - date1.getTime()) / 60000);
}

/**
 * Formats a duration expressed in minutes into a human readable string,
 * splitting hours and minutes when applicable.
 *
 * Examples (long form, the default):
 *   15  -> "15 min"
 *   60  -> "1 hr"
 *   75  -> "1 hr 15 min"
 *   120 -> "2 hr"
 *
 * Examples with `short: true`:
 *   15  -> "15m"
 *   60  -> "1h"
 *   75  -> "1h 15m"
 *
 * @param minutes  Total minutes (rounded, clamped to >= 0)
 * @param options  `short: true` for compact "h/m" form. Default is "hr/min".
 */
export function formatDurationMinutes(
  minutes: number,
  options?: { short?: boolean }
): string {
  const total = Math.max(0, Math.round(minutes));
  const short = options?.short ?? false;

  if (total === 0) return short ? '0m' : '0 min';

  const hours = Math.floor(total / 60);
  const mins = total % 60;

  if (short) {
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  }

  if (hours === 0) return `${mins} min`;
  if (mins === 0) return `${hours} hr`;
  return `${hours} hr ${mins} min`;
}

/**
 * Formats a countdown as MM:SS (e.g. 119 seconds -> "01:59").
 */
export function formatCountdownMmSs(totalSeconds: number): string {
  const seconds = Math.max(0, Math.ceil(totalSeconds));
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * Formats a time difference in a human-readable format
 * @param minutes The time difference in minutes
 * @returns A formatted string (e.g., "30 minutes early" or "15 minutes late")
 */
export function formatTimeDifference(minutes: number, status: 'early' | 'late' | 'on-time'): string {
  if (status === 'on-time') return 'on time';
  
  if (minutes < 1) return `less than a minute ${status}`;
  if (minutes === 1) return `1 minute ${status}`;
  
  return `${minutes} minutes ${status}`;
}

/**
 * Converts an "HH:MM" (or "HH:MM:SS") time-of-day string into total minutes since midnight.
 * Returns `null` when the input is missing or cannot be parsed.
 *
 * @param timeString - Time in 24-hour HH:MM format (e.g., "08:30", "17:00")
 * @returns Minutes since midnight, or `null` if invalid
 */
export function timeStringToMinutes(timeString: string | null | undefined): number | null {
  if (!timeString) return null;
  const parts = timeString.split(':');
  if (parts.length < 2) return null;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/**
 * Validates a 24-hour "H:MM", "HH:MM" or "HH:MM:SS" wall-clock time and returns it as "HH:MM".
 * Returns `null` for anything else, including out-of-range parts like "25:00" or "09:75".
 *
 * @param value - Candidate time string (e.g. "9:30", "17:00:00")
 * @returns Zero-padded "HH:MM", or `null` if invalid
 */
export function normalizeClockTime(value: string | null | undefined): string | null {
  const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec((value ?? '').trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59 || (match[3] !== undefined && Number(match[3]) > 59)) return null;
  return `${String(hours).padStart(2, '0')}:${match[2]}`;
}

/**
 * Converts total minutes since midnight back into a zero-padded "HH:MM" 24-hour string.
 * Values outside [0, 1440) are wrapped into the day so callers always get a well-formed string.
 *
 * @param minutes - Minutes since midnight
 * @returns A 24-hour HH:MM string (e.g. 510 -> "08:30")
 */
export function minutesToTimeString(minutes: number): string {
  const safe = ((Math.round(minutes) % 1440) + 1440) % 1440;
  const hh = Math.floor(safe / 60).toString().padStart(2, '0');
  const mm = (safe % 60).toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Subtracts a list of blocked windows from a single base window, returning the
 * remaining available windows in chronological order. All values are "HH:MM"
 * (or "HH:MM:SS") wall-clock strings; results are emitted as "HH:MM".
 *
 * This mirrors the delta model used by the backend availability service and by
 * `getEffectiveDayAvailability`: an UNAVAILABLE block is a subtraction layered
 * on the day's baseline. A block covering the whole base yields no windows; a
 * smaller block splits the base into the spans before and after it.
 *
 * @param base - The baseline available window, e.g. `{ start: "08:00", end: "17:00" }`.
 * @param blocks - Blocked sub-windows to carve out of the base.
 * @returns 0–N available windows remaining after subtraction.
 */
export function subtractWindows(
  base: { start: string; end: string },
  blocks: Array<{ start: string; end: string }>,
): Array<{ start: string; end: string }> {
  const baseStart = timeStringToMinutes(base.start);
  const baseEnd = timeStringToMinutes(base.end);
  if (baseStart === null || baseEnd === null || baseEnd <= baseStart) return [];

  // Normalize blocks to minutes, clamp to the base, drop empty/invalid ones,
  // then sort so we can sweep left→right.
  const normalized = blocks
    .map(b => ({ start: timeStringToMinutes(b.start), end: timeStringToMinutes(b.end) }))
    .filter((b): b is { start: number; end: number } => b.start !== null && b.end !== null && b.end > b.start)
    .map(b => ({ start: Math.max(b.start, baseStart), end: Math.min(b.end, baseEnd) }))
    .filter(b => b.end > b.start)
    .sort((a, b) => a.start - b.start);

  const result: Array<{ start: string; end: string }> = [];
  let cursor = baseStart;
  for (const block of normalized) {
    if (block.start > cursor) {
      result.push({ start: minutesToTimeString(cursor), end: minutesToTimeString(block.start) });
    }
    cursor = Math.max(cursor, block.end);
  }
  if (cursor < baseEnd) {
    result.push({ start: minutesToTimeString(cursor), end: minutesToTimeString(baseEnd) });
  }
  return result;
}

/**
 * Normalizes a wall-clock time string to a bare "HH:MM". The backend
 * serializes TimeSpans as "HH:MM:SS", so this must be applied before
 * comparing API times against "HH:MM" values used in the UI.
 *
 * @param timeString - Time in "HH:MM" or "HH:MM:SS" format
 * @returns The "HH:MM" prefix, or `null` when the input is missing/too short
 */
export function normalizeWallClockTime(timeString: string | null | undefined): string | null {
  if (!timeString) return null;
  const trimmed = timeString.trim();
  return trimmed.length >= 5 ? trimmed.slice(0, 5) : null;
}

/**
 * Converts a time string from 24-hour format to 12-hour format with AM/PM
 * @param timeString - Time in HH:MM format (e.g., "15:15", "09:30")
 * @returns A formatted time string in 12-hour format (e.g., "3:15 PM", "9:30 AM")
 */
export function formatTimeString(timeString: string | undefined): string {
  if (!timeString) return 'TBD';
  
  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr, 10);
  
  if (isNaN(hours) || isNaN(minutes)) return timeString;
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Compact 12-hour format suitable for short inline labels. Drops the colon
 * and minutes when they are zero so the label reads as a clean "8AM"
 * instead of "8:00 AM".
 *
 * Examples:
 *   "08:00" -> "8AM"
 *   "08:30" -> "8:30AM"
 *   "17:00" -> "5PM"
 *   "17:30" -> "5:30PM"
 *   "00:00" -> "12AM"
 *   "12:00" -> "12PM"
 *
 * @param timeString - Time in 24-hour HH:MM (or HH:MM:SS) format
 * @returns The compact 12-hour label, or the input back if it can't be parsed
 */
export function formatTimeStringCompact(timeString: string | null | undefined): string {
  if (!timeString) return '';
  const [hoursStr, minutesStr] = timeString.split(':');
  const hours = parseInt(hoursStr, 10);
  const minutes = parseInt(minutesStr ?? '0', 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return timeString;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  if (minutes === 0) return `${displayHours}${period}`;
  return `${displayHours}:${minutes.toString().padStart(2, '0')}${period}`;
}

/**
 * Parses free-form user-typed time-of-day input into a 24-hour "HH:MM" string.
 * Accepts formats like "9", "9:30", "935", "9:30 am", "12:35PM", "9.30pm".
 * When no AM/PM marker is present the hours are interpreted as 24-hour time.
 *
 * @param input - Raw text typed by the user
 * @returns A 24-hour "HH:MM" string, or `null` when the input can't be parsed
 */
export function parseTimeInput(input: string | null | undefined): string | null {
  if (!input) return null;
  const cleaned = input.trim().toLowerCase().replace(/\./g, ':').replace(/\s+/g, '');
  if (!cleaned) return null;

  // "935pm" style — digits with no separator get split as H:MM / HH:MM.
  const match = cleaned.match(/^(\d{1,2})(?::(\d{1,2})|(\d{2}))?(a|am|p|pm)?$/);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  const minuteStr = match[2] ?? match[3];
  const minutes = minuteStr !== undefined ? parseInt(minuteStr, 10) : 0;
  const period = match[4];

  if (!Number.isFinite(hours) || !Number.isFinite(minutes) || minutes > 59) return null;

  if (period) {
    if (hours < 1 || hours > 12) return null;
    hours = hours % 12;
    if (period.startsWith('p')) hours += 12;
  } else if (hours > 23) {
    return null;
  }

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Builds the list of selectable time-of-day options for dropdowns/pickers.
 * Each option carries the 24-hour "HH:MM" `value` used for storage and a
 * human 12-hour `label` (e.g. "8:30 AM") produced via {@link formatTimeString}.
 *
 * @param stepMinutes - Spacing between options in minutes (default 30)
 * @returns An ordered list of `{ value, label }` from 00:00 through the last
 *          step before midnight.
 */
export function generateTimeOptions(stepMinutes: number = 30): { value: string; label: string }[] {
  const step = stepMinutes > 0 ? stepMinutes : 30;
  const options: { value: string; label: string }[] = [];
  for (let minutes = 0; minutes < 1440; minutes += step) {
    const value = minutesToTimeString(minutes);
    options.push({ value, label: formatTimeString(value) });
  }
  return options;
}

/**
 * Adds (or subtracts when negative) whole days to a "YYYY-MM-DD" date key
 * and returns the resulting "YYYY-MM-DD" string. Useful when iterating
 * calendar dates as strings (e.g. fanning a multi-day external event into
 * one entry per day) without juggling `Date` objects and DST boundaries.
 *
 * Returns the original input unchanged when it cannot be parsed.
 *
 * @param dateKey - Source date in YYYY-MM-DD format
 * @param days - Number of days to add (negative to subtract)
 */
/**
 * Returns the JS weekday index (0 = Sunday … 6 = Saturday) for a "YYYY-MM-DD"
 * calendar date key. Uses UTC math so the weekday always matches the printed
 * calendar date, independent of browser or tenant timezone.
 */
export function getWeekdayFromDateKey(dateKey: string): number {
  const m = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 0;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (![y, mo, d].every(Number.isFinite)) return 0;
  return new Date(Date.UTC(y, mo - 1, d)).getUTCDay();
}

/**
 * Formats a "YYYY-MM-DD" key as a weekday + month + day label (e.g. "Wed, Sep 23").
 * UTC math so the label always matches the printed calendar date, regardless of the
 * browser timezone. Use this (never a model-computed weekday) whenever a Routy tool
 * result mentions a date — the LLM mislabels weekdays for dates it has to compute.
 */
export function formatDateKeyMedium(dateKey: string): string {
  const m = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateKey;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))));
}

export function addDaysToDateKey(dateKey: string, days: number): string {
  const m = dateKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return dateKey;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (![y, mo, d].every(Number.isFinite)) return dateKey;
  // UTC math avoids DST gaps / overlaps so "+1 day" is always 24h.
  const next = new Date(Date.UTC(y, mo - 1, d) + days * 86400000);
  const ny = next.getUTCFullYear();
  const nm = String(next.getUTCMonth() + 1).padStart(2, '0');
  const nd = String(next.getUTCDate()).padStart(2, '0');
  return `${ny}-${nm}-${nd}`;
}

/**
 * Returns the whole-day difference between two "YYYY-MM-DD" date keys
 * (`toKey - fromKey`). Positive when `toKey` is later than `fromKey`,
 * negative when earlier. Uses UTC math so the result is unaffected by DST.
 *
 * Returns 0 when either key cannot be parsed.
 */
export function daysBetweenDateKeys(fromKey: string, toKey: string): number {
  const a = fromKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const b = toKey.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!a || !b) return 0;
  const aUtc = Date.UTC(Number(a[1]), Number(a[2]) - 1, Number(a[3]));
  const bUtc = Date.UTC(Number(b[1]), Number(b[2]) - 1, Number(b[3]));
  return Math.round((bUtc - aUtc) / 86400000);
}

/**
 * Adds (or subtracts when negative) minutes to a "HH:MM" time-of-day string
 * and returns the resulting "HH:MM" string. Wraps within the day if the
 * result lands outside [0, 1440).
 *
 * Returns the original input unchanged when it cannot be parsed.
 *
 * @param timeString - Source time in 24-hour HH:MM format
 * @param minutes - Number of minutes to add (negative to subtract)
 */
export function addMinutesToTimeString(
  timeString: string | null | undefined,
  minutes: number
): string {
  if (!timeString) return '';
  const total = timeStringToMinutes(timeString);
  if (total === null) return timeString;
  return minutesToTimeString(total + minutes);
}

/**
 * Adds (or subtracts when negative) minutes to a 12-hour display time string
 * (e.g. "9:00 AM") and returns the shifted value in the same 12-hour format.
 * Operates purely on the clock string so it is timezone-safe — use it when you
 * already have a display-formatted time and only need to move it by a known
 * number of minutes (e.g. recomputing an end time after a duration change).
 * Wraps within the day if the result lands outside [0, 1440).
 *
 * Returns the original input unchanged when it cannot be parsed.
 *
 * @param displayTime - Source time in 12-hour "h:mm AM/PM" format
 * @param minutes - Number of minutes to add (negative to subtract)
 */
export function shiftDisplayTime(
  displayTime: string | null | undefined,
  minutes: number
): string {
  if (!displayTime) return '';
  const m = displayTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return displayTime;
  let hours = parseInt(m[1], 10);
  const mins = parseInt(m[2], 10);
  const period = m[3].toUpperCase();
  if (period === 'PM' && hours !== 12) hours += 12;
  if (period === 'AM' && hours === 12) hours = 0;
  const total = (((hours * 60 + mins + minutes) % 1440) + 1440) % 1440;
  const hh = Math.floor(total / 60)
    .toString()
    .padStart(2, '0');
  const mm = (total % 60).toString().padStart(2, '0');
  return formatTimeString(`${hh}:${mm}`);
}

/**
 * Builds the list of time-of-day options for a 24-hour day at `stepMinutes`
 * increments. Each entry has a 24h `value` (HH:MM) and a 12h `label` ready for
 * display ("8:00 AM"). Use this for time-selection menus instead of inlining
 * AM/PM formatting at call sites.
 *
 * @param stepMinutes - Increment between options (default 30)
 */
export function getDayTimeOptions(
  stepMinutes: number = 30
): Array<{ value: string; label: string }> {
  if (!Number.isFinite(stepMinutes) || stepMinutes <= 0) return [];
  const total = Math.floor((24 * 60) / stepMinutes);
  return Array.from({ length: total }, (_, i) => {
    const value = minutesToTimeString(i * stepMinutes);
    return { value, label: formatTimeString(value) };
  });
}

/**
 * Maps a time string (HH:MM format) to a preferred time of day category
 * Used for prepopulating the "Preferred Time of Day" field in booking forms
 * 
 * @param timeString - Time in HH:MM format (e.g., "09:30", "14:15")
 * @returns Preferred time of day value: "09:00" (Morning), "13:00" (Afternoon), "17:00" (Evening), or "" (Flexible)
 */
export function getPreferredTimeOfDay(timeString: string): string {
  if (!timeString) return '';
  
  const [hourStr] = timeString.split(':');
  const hour = parseInt(hourStr, 10);
  
  // Morning: 8AM - 12PM (8-11)
  if (hour >= 8 && hour < 12) {
    return '09:00';
  }
  
  // Afternoon: 12PM - 5PM (12-16)
  if (hour >= 12 && hour < 17) {
    return '13:00';
  }
  
  // Evening: 5PM - 8PM (17-19)
  if (hour >= 17 && hour < 20) {
    return '17:00';
  }
  
  // Outside normal hours - return flexible
  return '';
}

/**
 * Converts a Date object to preferred time of day category
 *
 * @param date - Date object
 * @param timezone - Optional IANA timezone (e.g., "America/New_York"). Uses browser timezone when omitted.
 * @returns Preferred time of day value: "09:00" (Morning), "13:00" (Afternoon), "17:00" (Evening), or "" (Flexible)
 */
export function getPreferredTimeOfDayFromDate(date: Date, timezone?: string): string {
  let hour: number;

  if (timezone) {
    // Extract the hour in the specified timezone using Intl.DateTimeFormat
    const parts = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      hour12: false,
      timeZone: timezone,
    }).formatToParts(date);
    const hourPart = parts.find((p) => p.type === 'hour');
    hour = hourPart ? parseInt(hourPart.value, 10) : date.getHours();
  } else {
    hour = date.getHours();
  }

  // Morning: 8AM - 12PM (8-11)
  if (hour >= 8 && hour < 12) {
    return '09:00';
  }

  // Afternoon: 12PM - 5PM (12-16)
  if (hour >= 12 && hour < 17) {
    return '13:00';
  }

  // Evening: 5PM - 8PM (17-19)
  if (hour >= 17 && hour < 20) {
    return '17:00';
  }

  // Outside normal hours - return flexible
  return '';
}

/**
 * Formats a date as a human-readable relative time string
 * Provides granular, accurate timestamps for better UX
 * 
 * Examples:
 * - "Just now" (< 5 minutes)
 * - "5 minutes ago" (5-59 minutes)
 * - "1 hour ago" (1 hour)
 * - "2 hours ago" (2-23 hours)
 * - "Yesterday" (24-47 hours)
 * - "2 days ago" (2-6 days)
 * - Date format for older items
 * 
 * @param date - The date to format (Date, string, or null/undefined)
 * @param options - Optional configuration
 * @returns A human-readable relative time string
 */
export function formatRelativeTime(
  date: Date | string | null | undefined,
  options?: {
    includeTime?: boolean;      // Include time for dates > 7 days old
    shortFormat?: boolean;      // Use abbreviated format (e.g., "5m ago" vs "5 minutes ago")
    allowFuture?: boolean;      // Handle future dates gracefully
    timezone?: string;          // IANA timezone for display and "today" boundary (e.g., "America/New_York"). Defaults to browser timezone.
  }
): string {
  if (!date) return 'No date';
  
  // Parse the date - handle UTC timestamps from .NET backend that lack 'Z' suffix
  let parsedDate: Date;
  if (date instanceof Date) {
    parsedDate = date;
  } else {
    // .NET backend sends UTC timestamps like "2026-02-04T19:34:06.6263446" without 'Z'.
    // Without 'Z', JS Date() interprets as local time, causing timestamps to appear
    // hours in the future. Append 'Z' to force UTC interpretation when the string
    // looks like an ISO timestamp without a timezone indicator.
    let dateStr = date;
    if (typeof dateStr === 'string' && dateStr.includes('T') && !dateStr.endsWith('Z') && !dateStr.includes('+') && !dateStr.includes('-', dateStr.indexOf('T'))) {
      dateStr = dateStr + 'Z';
    }
    parsedDate = new Date(dateStr);
  }
  
  // Validate the date
  if (isNaN(parsedDate.getTime())) return 'Invalid date';
  
  const now = new Date();
  const diffMs = now.getTime() - parsedDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  const short = options?.shortFormat ?? false;
  
  // Handle future dates (e.g., due to minor clock skew)
  if (diffMs < 0) {
    if (!options?.allowFuture) {
      // If within 10 minutes in the future, treat as "Just now"
      const minutesInFuture = Math.abs(diffMinutes);
      if (minutesInFuture < 10) {
        return 'Just now';
      }
      // For dates further in the future, show the actual date
      return parsedDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        ...(options?.timezone ? { timeZone: options.timezone } : {}),
      });
    }
    // Handle future dates if allowed
    const absMinutes = Math.abs(diffMinutes);
    const absHours = Math.abs(diffHours);
    const absDays = Math.abs(diffDays);
    
    if (absMinutes < 60) {
      return short ? `in ${absMinutes}m` : `in ${absMinutes} minute${absMinutes !== 1 ? 's' : ''}`;
    }
    if (absHours < 24) {
      return short ? `in ${absHours}h` : `in ${absHours} hour${absHours !== 1 ? 's' : ''}`;
    }
    return short ? `in ${absDays}d` : `in ${absDays} day${absDays !== 1 ? 's' : ''}`;
  }
  
  // Less than 5 minutes ago
  if (diffMinutes < 5) {
    return 'Just now';
  }
  
  // 5-59 minutes ago
  if (diffMinutes < 60) {
    return short ? `${diffMinutes}m ago` : `${diffMinutes} minutes ago`;
  }
  
  const timeOptions: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  if (options?.timezone) timeOptions.timeZone = options.timezone;
  const time = parsedDate.toLocaleTimeString('en-US', timeOptions);

  // Determine "today" and the parsed date's calendar day in the requested timezone (or browser local).
  let today: Date;
  let dateDay: Date;
  if (options?.timezone) {
    // Extract YYYY-MM-DD for both now and parsedDate in the given timezone, then compare as UTC midnight.
    const toDateKey = (d: Date): string =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone: options.timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).format(d);
    const nowKey = toDateKey(now);
    const parsedKey = toDateKey(parsedDate);
    today = new Date(nowKey + 'T00:00:00Z');
    dateDay = new Date(parsedKey + 'T00:00:00Z');
  } else {
    today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    dateDay = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate());
  }
  
  // 1-6 hours ago
  if (diffHours < 6) {
    return short ? `${diffHours}h ago` : `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  }
  
  // Today (6+ hours ago)
  if (dateDay.getTime() === today.getTime()) {
    return short ? time : `Today at ${time}`;
  }
  
  // Yesterday
  if (dateDay.getTime() === today.getTime() - 86400000) {
    return 'Yesterday';
  }
  
  // 2-6 days ago
  if (diffDays < 7) {
    return short ? `${diffDays}d ago` : `${diffDays} days ago`;
  }
  
  // 1-4 weeks ago
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return short ? `${weeks}w ago` : (weeks === 1 ? 'A week ago' : `${weeks} weeks ago`);
  }
  
  // Older - show date
  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  if (parsedDate.getFullYear() !== now.getFullYear()) {
    dateOptions.year = 'numeric';
  }

  if (options?.timezone) dateOptions.timeZone = options.timezone;

  return parsedDate.toLocaleDateString('en-US', dateOptions);
}

/**
 * Formats a date as relative time with both relative and absolute strings
 * Useful for tooltips and accessible time displays
 *
 * @param date - The date to format
 * @param timezone - Optional IANA timezone (e.g., "America/New_York"). Defaults to browser timezone when omitted.
 * @returns Object with relative string and absolute formatted date
 */
/**
 * Returns the number of whole days that have elapsed from the provided date
 * until now. Positive values indicate the date is in the past (e.g. 7 means
 * "7 days ago"). Negative values indicate a future date.
 *
 * Handles .NET UTC timestamps without 'Z' suffix to avoid timezone drift.
 * Use this instead of inlining `(Date.now() - d.getTime()) / 86400000` in
 * components / managers / pages.
 *
 * @param date - The date to measure elapsed time from
 * @returns Whole days elapsed, or null if the input is missing/invalid
 */
export function getDaysSince(date: Date | string | null | undefined): number | null {
  const until = getDaysUntil(date);
  if (until === null) return null;
  return -until;
}

/**
 * Returns the number of whole days from now until the provided date.
 * Negative values indicate the date has already passed.
 *
 * Handles .NET UTC timestamps without 'Z' suffix to avoid timezone drift.
 *
 * @param date - The future (or past) date
 * @returns Whole-day difference, or null if the input is missing/invalid
 */
export function getDaysUntil(date: Date | string | null | undefined): number | null {
  if (!date) return null;

  let parsedDate: Date;
  if (date instanceof Date) {
    parsedDate = date;
  } else {
    let dateStr = date;
    if (
      typeof dateStr === 'string' &&
      dateStr.includes('T') &&
      !dateStr.endsWith('Z') &&
      !dateStr.includes('+') &&
      !dateStr.includes('-', dateStr.indexOf('T'))
    ) {
      dateStr = dateStr + 'Z';
    }
    parsedDate = new Date(dateStr);
  }

  if (isNaN(parsedDate.getTime())) return null;

  const diffMs = parsedDate.getTime() - new Date().getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export function formatRelativeTimeWithAbsolute(
  date: Date | string | null | undefined,
  timezone?: string
): { relative: string; absolute: string } {
  if (!date) {
    return { relative: 'No date', absolute: 'No date' };
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (isNaN(parsedDate.getTime())) {
    return { relative: 'Invalid date', absolute: 'Invalid date' };
  }

  const absoluteOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  if (timezone) absoluteOptions.timeZone = timezone;

  const absolute = parsedDate.toLocaleDateString('en-US', absoluteOptions);

  return {
    relative: formatRelativeTime(date, timezone ? { timezone } : undefined),
    absolute,
  };
}

/**
 * Length of the "live booking" activity window after a customer's last
 * progress event. Mirrors the mobile helper so the 5-minute live indicator
 * stays in sync between web and mobile.
 */
const LIVE_BOOKING_WINDOW_MS = 5 * 60 * 1000;

/**
 * Returns how many whole seconds remain in the 5-minute "live booking"
 * activity window after the provided reference timestamp. The reference is
 * typically `request.lastModificationTime || request.date`. A reference in
 * the future is treated as "just created" (full 5-minute countdown). Returns
 * `0` when the reference is missing or the window has elapsed.
 */
export function getLiveBookingRemainingSeconds(
  referenceDate: Date | null | undefined,
  currentTime: Date = new Date()
): number {
  if (!referenceDate) return 0;
  const elapsedMs = currentTime.getTime() - referenceDate.getTime();
  const adjustedElapsedMs = Math.max(0, elapsedMs);
  const remainingMs = LIVE_BOOKING_WINDOW_MS - adjustedElapsedMs;
  return Math.max(0, Math.ceil(remainingMs / 1000));
}
